import { useTranslation } from "react-i18next";
import { type FormInstance } from "antd";
import { createStyles } from "antd-style";

import { Form } from "@av/components/atoms/form/Form";
import { Modal } from "@av/components/atoms/Modal";
import { Select } from "@av/components/atoms/Select";
import { mapErrors } from "../../../../../services/config/extract-validation-errors";

import type { ApiError, SelectOption } from "../../../../../services/admin-settings/type";

export type AssignBulkUserFormValues = {
  securityGroups: string[];
  users: string[];
};

type BulkAssignUserModalProps = {
  open: boolean;
  userOptions: SelectOption[] | undefined;
  securityGroupOptions: SelectOption[] | undefined;
  onCancel: () => void;
  onSubmit: (values: AssignBulkUserFormValues) => void;
  form: FormInstance<AssignBulkUserFormValues>;
  serverError: ApiError | null;
  isSubmitting: boolean;
};

const useStyles = createStyles(({ css, token }) => ({
  form: css`
    .ant-form-item {
      margin-bottom: ${token.margin}px;
    }

    .ant-form-item-row {
      align-items: center;
    }

    .ant-form-item-label {
      padding-bottom: 0;
      text-align: left;
    }

    .ant-form-item-label > label {
      height: auto;
      color: ${token.colorTextSecondary};
      font-size: ${token.fontSizeLG}px;
    }
  `,
}));

export function BulkAssignUserModal({
  open,
  securityGroupOptions,
  userOptions,
  onCancel,
  onSubmit,
  form,
  serverError,
  isSubmitting,
}: BulkAssignUserModalProps) {
  const { t } = useTranslation();
  const { styles } = useStyles();

  return (
    <Modal
      testIdPrefix="admin-settings-bulk-assign-user"
      title={t("platform.adminSettings.users_dialog_bulk_title")}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      afterClose={() => form.resetFields()}
      okText={t("platform.adminSettings.users_dialog_bulk_assign")}
      okButtonProps={{ loading: isSubmitting }}
      cancelText={t("common.domainShared.global_cancel")}
      forceRender
      width={772}
    >
      <Form
        serverErrors={mapErrors(t, serverError)}
        form={form}
        labelCol={{ flex: "150px" }}
        wrapperCol={{ flex: 1 }}
        layout="horizontal"
        requiredMark={false}
        onFinish={onSubmit}
        className={styles.form}
      >
        <Form.Item
          testIdPrefix="admin-settings-bulk-user-dialog-users"
          name="users"
          label={t("platform.adminSettings.users_dialog_users")}
        >
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            options={userOptions}
            placeholder={t("platform.adminSettings.selectPlaceholder_users")}
            testIdPrefix="admin-settings-bulk-user-list"
          />
        </Form.Item>
        <Form.Item
          testIdPrefix="admin-settings-user-dialog-security-group"
          label={t("platform.adminSettings.users_dialog_securityGroup")}
          name="securityGroups"
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            testIdPrefix="admin-settings-bulk-user-security-group"
            placeholder={t("platform.adminSettings.selectPlaceholder_securityGroups")}
            mode="multiple"
            options={securityGroupOptions}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
